import React, { useEffect, useState } from "react"
import { Modal, Form, Input, Select, Button, DatePicker, message } from "antd"
import dayjs from "dayjs"
import { translations } from "../../utils/Translation/staticTranslation"
import { PlansApi } from "../../Apis/plansApi"
import { DashboardUsersApi } from "../../Apis/dashboardUsersApi"
import {
  parseAdminPlansListResponse,
  filterPlansForDashboardUserCreate,
  mapPlansToSelectOptions,
} from "../../utils/plansPricingNormalize"
import { getStoreApiErrorMessage } from "../../helper/storeApiErrorMessage"
import { isValidCreateUserEmail } from "../../utils/createRolesHelpers"
import "../../styles/Pages/create-roles.css"

const CreateDashboardUserModal = ({ open, onClose, onSuccess }) => {
  const lang = localStorage.getItem("language") || "ar"
  const t = translations[lang]?.allUsersPage || {}
  const passwordMinLengthMessage =
    translations[lang]?.reset_password?.validation_2 ||
    "Password must be at least 8 characters long"
  const [form] = Form.useForm()
  const [submitting, setSubmitting] = useState(false)
  const [plansLoading, setPlansLoading] = useState(false)
  const [planOptions, setPlanOptions] = useState([])

  useEffect(() => {
    if (!open) return
    form.resetFields()
    setSubmitting(false)

    let cancelled = false
    const loadPlans = async () => {
      setPlansLoading(true)
      try {
        const response = await PlansApi.getPlansList({
          status: "active",
          planCategory: "standard",
          page: 1,
          pageSize: 200,
        })
        if (cancelled) return
        const raw = filterPlansForDashboardUserCreate(
          parseAdminPlansListResponse(response?.data)
        )
        setPlanOptions(mapPlansToSelectOptions(raw, lang))
      } catch {
        if (!cancelled) {
          setPlanOptions([])
          message.error(t.error_plans_load || "Could not load plans")
        }
      } finally {
        if (!cancelled) setPlansLoading(false)
      }
    }

    loadPlans()
    return () => {
      cancelled = true
    }
  }, [open, form, lang, t.error_plans_load])

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields()

      const payload = {
        email: values.email.trim(),
        password: values.password,
        name_english: values.name_english.trim(),
      }

      if (values.plan_id != null && values.plan_id !== "") {
        payload.plan_id = values.plan_id
      }

      if (values.name_arabic?.trim()) {
        payload.name_arabic = values.name_arabic.trim()
      }
      if (values.account_access_expires_at) {
        payload.account_access_expires_at = dayjs(
          values.account_access_expires_at
        )
          .endOf("day")
          .toISOString()
      }

      setSubmitting(true)
      const response = await DashboardUsersApi.create(payload)
      const responseData = response?.data
      if (responseData?.success === false) {
        message.error(
          getStoreApiErrorMessage(responseData, lang, {
            generic: t.error_create_failed || "Could not create user",
          })
        )
        return
      }
      message.success(t.success_created || "User created successfully")
      onClose()
      onSuccess?.()
    } catch (error) {
      if (error?.errorFields) return
      message.error(
        getStoreApiErrorMessage(error?.response?.data, lang, {
          generic: t.error_create_failed || "Could not create user",
        })
      )
    } finally {
      setSubmitting(false)
    }
  }

  const isRTL = lang === "ar"

  return (
    <Modal
      title={
        <div className="create-user-modal-title-wrap">
          <div className="create-user-modal-title-main">
            {t.modal_title || "Create dashboard user"}
          </div>
          <div className="create-user-modal-title-sub">
            {t.modal_subtitle || "Create a new user and assign a plan."}
          </div>
        </div>
      }
      open={open}
      onCancel={() => {
        if (!submitting) onClose()
      }}
      width={560}
      centered
      destroyOnClose
      footer={null}
      className="create-user-modal create-dashboard-user-modal"
      maskClosable={false}
    >
      <div className="create-user-modal-inner" dir={isRTL ? "rtl" : "ltr"}>
        <Form
          form={form}
          layout="vertical"
          autoComplete="off"
          className="create-dashboard-user-modal-form"
        >
          <Form.Item
            name="name_english"
            label={t.name_english || "Name (English)"}
            rules={[
              {
                required: true,
                message: t.validation_name_required || "Name is required",
              },
            ]}
          >
            <Input
              className="create-user-modal-input"
              size="large"
              placeholder={t.name_english_placeholder}
            />
          </Form.Item>

          <Form.Item
            name="name_arabic"
            label={t.name_arabic || "Name (Arabic)"}
          >
            <Input
              className="create-user-modal-input"
              size="large"
              placeholder={t.name_arabic_placeholder}
            />
          </Form.Item>

          <Form.Item
            name="email"
            label={t.email || "Email"}
            rules={[
              {
                required: true,
                message: t.validation_email_required || "Email is required",
              },
              {
                validator: (_, value) => {
                  const trimmed = String(value || "").trim()
                  if (!trimmed) return Promise.resolve()
                  if (!isValidCreateUserEmail(trimmed)) {
                    return Promise.reject(
                      new Error(
                        t.validation_email_invalid ||
                          "Please enter a valid email address"
                      )
                    )
                  }
                  return Promise.resolve()
                },
              },
            ]}
          >
            <Input
              className="create-user-modal-input"
              size="large"
              type="email"
              placeholder={t.email_placeholder}
              autoComplete="off"
            />
          </Form.Item>

          <Form.Item
            name="password"
            label={t.password || "Password"}
            validateTrigger={["onChange", "onBlur"]}
            rules={[
              {
                required: true,
                message: t.validation_password_required || "Password is required",
              },
              {
                min: 8,
                message: passwordMinLengthMessage,
              },
            ]}
          >
            <Input.Password
              className="create-user-modal-input"
              size="large"
              placeholder={t.password_placeholder}
              autoComplete="new-password"
            />
          </Form.Item>

          <Form.Item name="plan_id" label={t.plan || "Plan"}>
            <Select
              className="create-user-modal-select"
              size="large"
              placeholder={t.plan_placeholder}
              options={planOptions}
              loading={plansLoading}
              disabled={plansLoading && planOptions.length === 0}
              allowClear
            />
          </Form.Item>

          <Form.Item
            name="account_access_expires_at"
            label={t.expiry || "Access expires"}
          >
            <DatePicker
              className="create-user-modal-input"
              size="large"
              style={{ width: "100%" }}
              placeholder={t.expiry_placeholder}
              format="YYYY-MM-DD"
            />
          </Form.Item>
        </Form>

        <div className="create-user-modal-footer create-dashboard-user-modal-footer">
          <Button
            type="default"
            className="create-user-modal-cancel"
            onClick={onClose}
            disabled={submitting}
          >
            {t.cancel || "Cancel"}
          </Button>
          <Button
            className="create-roles-action-btn create-user-modal-submit"
            onClick={handleSubmit}
            loading={submitting}
            disabled={submitting}
          >
            {t.submit || "Create user"}
          </Button>
        </div>
      </div>
    </Modal>
  )
}

export default CreateDashboardUserModal
